import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { streamLogs, useApi, useFetcher, type Deployment, type Job, type PipelineDetail } from "../api.js";
import { useAuth } from "../auth.js";
import { useOrg } from "../components/Layout.js";
import { PipelineGraph } from "../components/PipelineGraph.js";
import { Button, Card, ErrorBox, Loading, PageHeader, Sha, StatusBadge, Time, duration, useAction } from "../components/ui.js";

/** Providers whose deployments upload a build to an app store. */
const STORE_PROVIDERS = new Set(["apple", "google-play"]);

type View = "graph" | "list";
const VIEW_KEY = "repu.pipelineView";

function savedView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === "list" ? "list" : "graph";
  } catch {
    return "graph";
  }
}

const LIVE = new Set(["queued", "running", "awaiting-approval", "pending-approval", "approved", "created"]);

export function PipelinePage() {
  const org = useOrg();
  const { id = "" } = useParams();
  const fetcher = useFetcher();
  const navigate = useNavigate();
  const [refresh, setRefresh] = useState(true);
  const pipeline = useApi<PipelineDetail>(`/v1/orgs/${org}/pipelines/${id}`, refresh ? { refreshMs: 3000 } : {});
  const [selected, setSelected] = useState<string>();
  const [view, setView] = useState<View>(savedView);
  const chooseView = (next: View): void => {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      /* the choice just is not remembered */
    }
  };
  useEffect(() => {
    if (pipeline.data && !LIVE.has(pipeline.data.status)) setRefresh(false);
  }, [pipeline.data]);
  const cancel = useAction(() => fetcher(`/v1/orgs/${org}/pipelines/${id}/cancel`, { method: "POST" }), () => {
    pipeline.reload();
  });
  const retry = useAction(async () => {
    const p = await fetcher<PipelineDetail>(`/v1/orgs/${org}/pipelines/${id}/retry`, { method: "POST" });
    void navigate(`/o/${org}/pipelines/${p.id}`);
  });

  if (pipeline.loading) return <Loading />;
  if (!pipeline.data) return <ErrorBox error={pipeline.error} />;
  const p = pipeline.data;
  const active = p.jobs.find((j) => j.id === selected) ?? p.jobs.find((j) => j.status === "running") ?? p.jobs.find((j) => j.status === "failed") ?? p.jobs[0];
  return (
    <>
      <PageHeader
        title={
          <>
            {p.mode === "deploy" ? `Deploy to ${p.environment ?? ""}` : p.jobs[0]?.kind === "store-release" ? "App store release" : "CI"} <StatusBadge status={p.status} />
          </>
        }
        subtitle={
          <>
            <Link to={`/o/${org}/projects/${p.project}`}>{p.project}</Link> · <Sha sha={p.commitSha} /> {p.ref} · {p.trigger.type} · <Time iso={p.createdAt} />
            {p.deploymentId && (
              <>
                {" "}
                · <Link to={`/o/${org}/deployments/${p.deploymentId}`}>deployment</Link>
              </>
            )}
          </>
        }
        actions={
          <>
            <span className="view-toggle" role="group" aria-label="Pipeline view">
              {(["graph", "list"] as const).map((v) => (
                <button key={v} aria-pressed={view === v} onClick={() => { chooseView(v); }}>
                  {v === "graph" ? "Graph" : "List"}
                </button>
              ))}
            </span>
            {LIVE.has(p.status) && (
              <Button variant="danger" busy={cancel.busy} onClick={() => { void cancel.run(); }}>
                Cancel
              </Button>
            )}
            {!LIVE.has(p.status) && p.mode === "ci" && p.jobs[0]?.kind !== "store-release" && (
              <Button busy={retry.busy} onClick={() => { void retry.run(); }}>
                Re-run
              </Button>
            )}
          </>
        }
      />
      <ErrorBox error={pipeline.error ?? cancel.error ?? retry.error} />
      {view === "graph" ? (
        <>
          <PipelineGraph jobs={p.jobs} selectedId={active?.id} onSelect={setSelected} />
          {active && <JobDetail job={active} org={org} />}
        </>
      ) : (
        <div className="split">
          <div className="job-list" role="list">
            {p.jobs.map((j) => (
              <button key={j.id} role="listitem" className={`job-item ${active?.id === j.id ? "selected" : ""}`} onClick={() => { setSelected(j.id); }}>
                <StatusBadge status={j.status} />
                <span className="job-name">{j.name}</span>
                <span className="muted small">{duration(j.startedAt, j.finishedAt)}</span>
                {j.needs.length > 0 && <span className="muted small needs">after {j.needs.join(", ")}</span>}
              </button>
            ))}
          </div>
          <div>{active && <JobDetail job={active} org={org} />}</div>
        </div>
      )}
      {Object.keys(p.reasons).length > 0 && (
        <Card title="Why these targets">
          <ul className="reasons">
            {Object.entries(p.reasons).map(([t, rs]) => (
              <li key={t}>
                <code>{t}</code> <span className="muted">— {rs.join("; ")}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

export function JobDetail({ job, org }: { job: Job; org: string }) {
  return (
    <Card
      title={
        <>
          {job.name} <StatusBadge status={job.status} />
        </>
      }
      actions={<span className="muted small">attempt {job.attempt} · {job.runner.os}{job.runnerId ? ` · ${job.runnerId}` : ""}</span>}
    >
      {job.error && <div className="error-box">{job.error}</div>}
      <ol className="steps">
        {job.steps.map((s) => (
          <li key={s.name} className={`step step-${s.status}`}>
            <span className="step-dot" aria-hidden />
            {s.name}
            {s.durationMs !== null && <span className="muted small"> {Math.round(s.durationMs / 100) / 10}s</span>}
            {s.status === "skipped" && <span className="muted small"> skipped</span>}
          </li>
        ))}
      </ol>
      {(job.artifactId ?? job.releaseRef) && (
        <p className="muted small">
          {job.artifactId && <>artifact <code>{job.artifactId}</code> </>}
          {job.releaseRef && <>release <code>{job.releaseRef}</code></>}
        </p>
      )}
      <LogViewer org={org} jobId={job.id} status={job.status} />
    </Card>
  );
}

export function LogViewer({ org, jobId, status }: { org: string; jobId: string; status: string }) {
  const auth = useAuth();
  const [text, setText] = useState("");
  const [error, setError] = useState<Error>();
  const box = useRef<HTMLPreElement>(null);
  const follow = useRef(true);
  const started = status !== "pending" && status !== "queued" && status !== "skipped";

  useEffect(() => {
    if (!started) return;
    setText("");
    const controller = new AbortController();
    void (async () => {
      try {
        await streamLogs(await auth.token(), org, jobId, (t) => { setText((prev) => prev + t); }, controller.signal);
      } catch (e) {
        if (!controller.signal.aborted) setError(e instanceof Error ? e : new Error(String(e)));
      }
    })();
    return () => {
      controller.abort();
    };
    // Restart only when the job changes or starts.
  }, [org, jobId, started, auth]);

  useEffect(() => {
    if (follow.current && box.current) box.current.scrollTop = box.current.scrollHeight;
  }, [text]);

  if (!started) return <p className="muted">Waiting for a runner…</p>;
  return (
    <>
      <ErrorBox error={error} />
      <pre
        className="log"
        ref={box}
        onScroll={(e) => {
          const el = e.currentTarget;
          follow.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
        }}
      >
        {text || "…"}
      </pre>
    </>
  );
}

export function JobPage() {
  const org = useOrg();
  const { id = "" } = useParams();
  const job = useApi<Job>(`/v1/orgs/${org}/jobs/${id}`, { refreshMs: 3000 });
  if (job.loading) return <Loading />;
  if (!job.data) return <ErrorBox error={job.error} />;
  return (
    <>
      <PageHeader title={job.data.name} subtitle={<Link to={`/o/${org}/pipelines/${job.data.pipelineId}`}>pipeline</Link>} />
      <JobDetail job={job.data} org={org} />
    </>
  );
}

export function DeploymentPage() {
  const org = useOrg();
  const { id = "" } = useParams();
  const fetcher = useFetcher();
  const navigate = useNavigate();
  const deployment = useApi<Deployment>(`/v1/orgs/${org}/deployments/${id}`, { refreshMs: 4000 });
  const [comment, setComment] = useState("");
  const act = useAction(
    (action: "approve" | "reject" | "cancel") =>
      fetcher(`/v1/orgs/${org}/deployments/${id}/${action}`, { method: "POST", body: action === "cancel" ? {} : comment ? { comment } : {} }),
    () => {
      deployment.reload();
    },
  );
  const store = useAction(async (action: "publish" | "check", target: string) => {
    const p = await fetcher<PipelineDetail>(`/v1/orgs/${org}/deployments/${id}/store/${action}`, { method: "POST", body: { target } });
    void navigate(`/o/${org}/pipelines/${p.id}`);
  });
  const rollback = useAction(async () => {
    const d = deployment.data;
    if (!d) return;
    const r = await fetcher<Deployment>(`/v1/orgs/${org}/projects/${d.project}/rollbacks`, { method: "POST", body: { environment: d.environment, to: d.id } });
    void navigate(`/o/${org}/deployments/${r.id}`);
  });

  if (deployment.loading) return <Loading />;
  if (!deployment.data) return <ErrorBox error={deployment.error} />;
  const d = deployment.data;
  const finished = ["succeeded", "failed", "rejected", "cancelled"].includes(d.status);
  return (
    <>
      <PageHeader
        title={
          <>
            {d.environment} <StatusBadge status={d.status} />
          </>
        }
        subtitle={
          <>
            <Link to={`/o/${org}/projects/${d.project}`}>{d.project}</Link> · <Sha sha={d.commitSha} /> {d.ref}
            {d.message && <> · {d.message}</>}
          </>
        }
        actions={
          <>
            {d.pipelineId && <Link className="btn btn-default" to={`/o/${org}/pipelines/${d.pipelineId}`}>Pipeline</Link>}
            {!finished && (
              <Button variant="danger" busy={act.busy} onClick={() => { void act.run("cancel"); }}>
                Cancel
              </Button>
            )}
            {d.status === "succeeded" && (
              <Button busy={rollback.busy} onClick={() => { if (window.confirm(`Redeploy this release to ${d.environment}?`)) void rollback.run(); }}>
                Roll back to this
              </Button>
            )}
          </>
        }
      />
      <ErrorBox error={act.error ?? rollback.error ?? store.error} />
      {d.status === "pending-approval" && (
        <Card title="Approval required">
          <p className="muted">Someone with release rights other than the requester must approve before this deploys.</p>
          <div className="inline-form">
            <input placeholder="Comment (required to reject)" value={comment} onChange={(e) => { setComment(e.target.value); }} />
            <Button variant="primary" busy={act.busy} onClick={() => { void act.run("approve"); }}>
              Approve
            </Button>
            <Button variant="danger" busy={act.busy} onClick={() => { void act.run("reject"); }}>
              Reject
            </Button>
          </div>
        </Card>
      )}
      <Card title="Targets">
        <table className="table">
          <thead>
            <tr>
              <th>Target</th>
              <th>Provider</th>
              <th>Status</th>
              <th>Release</th>
              <th>Artifact</th>
              <th>App store</th>
            </tr>
          </thead>
          <tbody>
            {d.targets.map((t) => (
              <tr key={t.address}>
                <td>
                  <code>{t.address}</code>
                </td>
                <td>
                  {t.provider} <span className="muted">{t.destination}</span>
                </td>
                <td>
                  <StatusBadge status={t.status} />
                </td>
                <td>
                  {t.releaseRef ? <code>{t.releaseRef}</code> : <span className="muted">—</span>}
                  {t.url && (
                    <div className="small">
                      <a href={t.url} target="_blank" rel="noreferrer">{t.url}</a>
                    </div>
                  )}
                  {t.details?.["rolloutId"] && <div className="muted small">rollout <code>{t.details["rolloutId"]}</code></div>}
                </td>
                <td>{t.artifactId ? <code>{t.artifactId}</code> : <span className="muted">—</span>}</td>
                <td>
                  {STORE_PROVIDERS.has(t.provider) && t.status === "succeeded" && t.releaseRef ? (
                    <span className="inline-form">
                      <Button
                        variant="primary"
                        busy={store.busy}
                        onClick={() => { if (window.confirm(`Publish ${t.releaseRef} from ${t.address} to the ${t.provider === "apple" ? "App Store" : "Play Store"}?`)) void store.run("publish", t.address); }}
                      >
                        Publish
                      </Button>
                      <Button busy={store.busy} onClick={() => { void store.run("check", t.address); }}>
                        Check published
                      </Button>
                    </span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Card title="History">
        <ol className="timeline">
          {d.history.map((h, i) => (
            <li key={i}>
              <StatusBadge status={h.status} /> <Time iso={h.at} />
              {h.reason && <span className="muted"> — {h.reason}</span>}
            </li>
          ))}
          {d.approvals.map((a) => (
            <li key={a.at}>
              <StatusBadge status={a.decision} /> by <code>{a.userId}</code> <Time iso={a.at} />
              {a.comment && <span className="muted"> — “{a.comment}”</span>}
            </li>
          ))}
        </ol>
      </Card>
    </>
  );
}
